import type { ParseKeys } from "i18next";
import { useTranslation } from "react-i18next";
// src/pages/PurchaseDetail.tsx
import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { apiGet, apiGetBlob, apiDelete, apiPost, apiPatch } from "../api/client";
import { canonicalVendorPayment, classifyVendorPaymentFailure } from "../utils/vendorPaymentIdempotency";
import { useVendorPaymentRecovery } from "../utils/vendorPaymentPersistence";
import { Button } from "../components/Button";
import { AppleComboBox } from "../components/AppleComboBox";
import { PurchaseInfoHeader } from "../components/PurchaseInfoHeader";
import { MemoSection } from "../components/MemoSection";
import { AppleCard } from "../components/AppleCard";
import { ArrowLeftIcon, XMarkIcon } from "@heroicons/react/24/solid";
import {
  EyeIcon,
  ArrowDownTrayIcon,
  PaperClipIcon,
} from "@heroicons/react/24/outline";
import { companyRoute } from "../utils/routeHelpers";
import { AuthMe } from "../types/auth";
import {
  ymdTodayLocal,
  formatAnyDateToLocale,
  parseYmdLocal,
} from "../utils/dateUtils";
import { formatMoney } from "../utils/formatNumber";
import { A } from "../styles/appleClasses";
import { AppleDateInput } from "../components/AppleDateInput";
import { AppleNumberInput } from "../components/AppleNumberInput";
import {
  extractApiDetailOutcome,
  formatClosedThroughDate,
  isClosedPeriodError,
  isDateClosed,
} from "../utils/closedPeriod";

/* =========================
   Types
   ========================= */
type BubbleType = "paid" | "partial" | "unpaid" | "voided";
type BubbleInfo = { label: string; bubbleType: BubbleType };

interface Vendor {
  id: number;
  name: string;
  email: string;
  mobile?: string | null;
}

interface PurchaseLine {
  id: number;
  product_id: number | null;
  product_name: string | null;
  qty: number;
  cost_price: number;
  discount?: number;
  total: number;
}

interface PurchaseSourceDocument {
  id: number;
  original_filename?: string | null;
  content_type?: string | null;
  byte_size?: number | null;
  created_at: string;
}

interface Purchase {
  id: number;
  date: string;
  vendor: Vendor | null;
  amount: number;
  status: string | null;
  lines: PurchaseLine[];
  due_date?: string;
  memo?: string | null;
  voided?: boolean;
  bill_id?: number;
  invoice_number?: string;
  invoice_id?: number | string;
  edited?: boolean;
  last_edited_at?: string;
  source_document_id?: number | null;
  source_document?: PurchaseSourceDocument | null;
}

interface VendorPayment {
  id: number;
  amount: number | string;
  vendor_payment_date: string;
  bill_id: number | string;
  payment_account_id?: number;
}

interface Account {
  id: number;
  name: string;
  kind: string;
  is_system?: boolean;
}

type Editability = {
  allowed: boolean;
  reasons: string[];
  editable_until?: string | null;
  now?: string | null;
  window_seconds_remaining?: number | null;
  pro_override?: boolean; // align with SaleDetail
};

/* =========================
   Helpers
   ========================= */

function ymdToTime(ymd: string) {
  const d = parseYmdLocal(ymd);
  return d ? d.getTime() : new Date(ymd).getTime();
}

function formatErrorDates(message: string) {
  return message.replace(/\b\d{4}-\d{2}-\d{2}\b/g, (ymd) =>
    formatAnyDateToLocale(ymd),
  );
}

function calcSubtotal(line: PurchaseLine) {
  // PURCHASE-LINE-ROUND-STAGE-1: render the backend-persisted line total
  // verbatim -- never recompute a stored accounting value in the browser
  // (a browser recompute would diverge from the canonical net-unit-first
  // amount the backend stored).
  return line.total;
}

function getPurchaseBubbles(
  purchase: Purchase | null,
  totalPaid: number,
): BubbleInfo[] {
  if (!purchase) return [];
  if (purchase.voided) return [{ label: "Voided", bubbleType: "voided" }];

  const isPaid =
    Math.abs(totalPaid - (purchase.amount || 0)) < 0.005 ||
    purchase.status?.toLowerCase() === "paid" ||
    purchase.status?.toLowerCase() === "closed";

  const isPartial = totalPaid > 0 && !isPaid;

  if (isPaid) return [{ label: "Paid", bubbleType: "paid" }];
  if (isPartial) return [{ label: "Partial", bubbleType: "partial" }];
  return [{ label: "Unpaid", bubbleType: "unpaid" }];
}

function parseMoneyInput(v?: string | number | null) {
  const s = String(v ?? "")
    .replace(/,/g, "")
    .trim();
  const n = parseFloat(s);
  return Number.isFinite(n) ? n : 0;
}

function toInputMoney(n: number) {
  const safe = Number.isFinite(n) ? n : 0;
  return String(Math.round(safe * 100) / 100);
}

function paymentAccountWeight(account: Account) {
  const name = String(account.name || "")
    .toLowerCase()
    .trim();
  const kind = String(account.kind || "")
    .toLowerCase()
    .trim();

  if (kind === "cash" || name.includes("cash")) return 10;

  if (
    kind === "bank" ||
    name.includes("bank") ||
    name.includes("fps") ||
    name.includes("transfer") ||
    name.includes("payme")
  ) {
    return 20;
  }

  return 80;
}

/* =========================
   Component
   ========================= */
// Display metadata is never persisted or passed to error classifiers.
type LocalPurchaseMessage = { key: ParseKeys; values?: Record<string, string | number>; closedDate?: string | null; field?: LocalPurchaseMessage; inner?: PurchaseMessage };
type PurchaseMessage = string | LocalPurchaseMessage;
const localMessage = (key: ParseKeys, values?: Record<string, string | number>): LocalPurchaseMessage => ({ key, values });
function localApiDetail(err: any): PurchaseMessage {
  const outcome = extractApiDetailOutcome(err);
  return outcome.kind === "raw" ? outcome.text : localMessage("sales.errors.generic");
}
function localClosedPeriod(closedDate?: string | null): LocalPurchaseMessage {
  return { key: "sales.errors.closedSubmit", closedDate };
}

export default function PurchaseDetail() {
  const { t } = useTranslation();
  const messageText = (message: PurchaseMessage | null | undefined): string => {
    if (message == null) return "";
    if (typeof message === "string") return message;
    const values = "closedDate" in message
      ? { date: message.closedDate ? formatClosedThroughDate(message.closedDate) : t("sales.errors.closedDate") }
      : { ...message.values, ...(message.field ? { field: messageText(message.field) } : {}), ...(message.inner !== undefined ? { error: messageText(message.inner) } : {}) };
    return t(message.key, values);
  };

  const navigate = useNavigate();
  const { id, company_id } = useParams<{ id: string; company_id: string }>();

  const [purchase, setPurchase] = useState<Purchase | null>(null);
  const [billId, setBillId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<PurchaseMessage | null>(null);
  const [closedThroughDate, setClosedThroughDate] = useState<string | null>(
    null,
  );

  // Payments / accounts
  const [vendorPayments, setVendorPayments] = useState<VendorPayment[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);

  // Add payment
  const [paymentDate, setPaymentDate] = useState(() => ymdTodayLocal());
  const [paymentAccountId, setPaymentAccountId] = useState<number | null>(null);
  const [paymentAmount, setPaymentAmount] = useState<string>("");
  const [paymentError, setPaymentError] = useState<PurchaseMessage | null>(null);

  const [paymentDocumentRoute, setPaymentDocumentRoute] = useState<string | null>(null);
  const routeIdentity = `${company_id}:${id}`;
  const routeIdentityRef = useRef(routeIdentity);
  routeIdentityRef.current = routeIdentity;
  useEffect(() => { setPaymentError(null); }, [routeIdentity]);
  const recovery = useVendorPaymentRecovery(
    company_id && paymentDocumentRoute === routeIdentity && purchase && String(purchase.id) === id ? Number(company_id) : null,
    paymentDocumentRoute === routeIdentity && purchase && String(purchase.id) === id && billId ? Number(billId) : null,
    !!purchase?.voided,
    body => {
      setPaymentDate(body.vendor_payment_date);
      setPaymentAmount(body.amount);
      setPaymentAccountId(body.payment_account_id);
    },
  );
  const paying = recovery.pending;
  const paymentBody = () => {
    const saved = recovery.ticket?.submittedPayload;
    const amount = parseMoneyInput(paymentAmount);
    if (saved && saved.vendor_payment_date === paymentDate &&
        saved.payment_account_id === paymentAccountId &&
        (saved.amount === paymentAmount || Number(saved.amount) === amount)) return saved;
    return { company_id: Number(company_id),
      ...canonicalVendorPayment(Number(billId), paymentDate, amount, paymentAccountId ?? 0) };
  };
  const editedPayment = !!recovery.ticket && !recovery.exact(paymentBody());
  const recoveryMessage = recovery.problem === "corrupt"
    ? localMessage("vendorPayments.retry.corrupt")
    : recovery.problem === "storage" ? localMessage("vendorPayments.retry.cantSave")
    : recovery.ticket && !paying ? localMessage("vendorPayments.retry.uncertain") : null;


  // Memo (SaleDetail-clone behavior)
  const [editingMemo, setEditingMemo] = useState(false);
  const [memoDraft, setMemoDraft] = useState("");
  const [memoSaving, setMemoSaving] = useState(false);
  const [memoError, setMemoError] = useState<PurchaseMessage | null>(null);

  // Delete
  const [showDelete, setShowDelete] = useState(false);
  const [deleteError, setDeleteError] = useState<PurchaseMessage | null>(null);

  // Scanned invoice (source document)
  const [sourceDocumentError, setSourceDocumentError] = useState<PurchaseMessage | null>(
    null,
  );
  const [sourceDocumentPreview, setSourceDocumentPreview] = useState<{
    url: string;
    contentType: string;
  } | null>(null);

  // Editability (server-side)
  const [ed, setEd] = useState<Editability | null>(null);

  // “Why can’t I edit?” modal
  const [showBlockedEdit, setShowBlockedEdit] = useState(false);
  const [blockedReasons, setBlockedReasons] = useState<PurchaseMessage[]>([]);

  // Auth
  const [me, setMe] = useState<AuthMe | null>(null);
  useEffect(() => {
    apiGet<AuthMe>("/auth/me")
      .then(setMe)
      .catch(() => setMe(null));
  }, []);

  /* ---- Stable refreshers ---- */
  const refreshPurchaseOnly = useCallback(async () => {
    if (!id || !company_id) return null;
    const route = `${company_id}:${id}`;
    const res = await apiGet<Purchase>(
      `/purchases/${id}?company_id=${company_id}&_=${Date.now()}`,
    );
    if (routeIdentityRef.current !== route) return null;
    setPaymentDocumentRoute(route);
    setPurchase(res);
    setBillId(res.bill_id ?? null);
    setMemoDraft(res.memo || "");
    return res;
  }, [id, company_id]);

  const refreshPurchaseAndPayments = useCallback(async () => {
    const route = routeIdentityRef.current;
    const updated = await refreshPurchaseOnly();
    if (!updated) return;

    // Payments
    const all = await apiGet<VendorPayment[]>(
      `/ar-ap/vendor-payments?company_id=${company_id}&_=${Date.now()}`,
    ).catch(() => []);
    if (routeIdentityRef.current !== route) return;
    const billPayments = (all || [])
      .filter((p) => String(p.bill_id) == String(updated.bill_id))
      .sort(
        (a, b) =>
          ymdToTime(a.vendor_payment_date) - ymdToTime(b.vendor_payment_date),
      );
    setVendorPayments(billPayments);

    // Editability
    const e = await apiGet<Editability>(
      `/purchases/${updated.id}/editability?company_id=${company_id}`,
    ).catch(() => null);
    if (e && routeIdentityRef.current === route) setEd(e);
  }, [company_id, refreshPurchaseOnly]);

  // Refetch on focus/visibility
  useEffect(() => {
    const onFocus = () => refreshPurchaseAndPayments();
    const onVis = () => {
      if (document.visibilityState === "visible") refreshPurchaseAndPayments();
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVis);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [refreshPurchaseAndPayments]);

  useEffect(() => {
    return () => {
      if (sourceDocumentPreview?.url) {
        URL.revokeObjectURL(sourceDocumentPreview.url);
      }
    };
  }, [sourceDocumentPreview]);

  useEffect(() => {
    if (!sourceDocumentPreview) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setSourceDocumentPreview(null);
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [sourceDocumentPreview]);

  /* ---- Initial loads ---- */
  useEffect(() => {
    setLoading(true);
    setError(null);
    refreshPurchaseOnly()
      .catch(() => setError(localMessage("purchases.errors.load")))
      .finally(() => setLoading(false));
  }, [refreshPurchaseOnly]);

  useEffect(() => {
    if (!company_id) return;
    apiGet<Account[]>(`/accounts?company_id=${company_id}`)
      .then((data) => setAccounts(data))
      .catch(() => setAccounts([]));
  }, [company_id]);

  useEffect(() => {
    if (!purchase || !company_id || String(purchase.id) !== id) return;
    const route = routeIdentityRef.current;
    apiGet<VendorPayment[]>(`/ar-ap/vendor-payments?company_id=${company_id}`)
      .then((all) => {
        if (routeIdentityRef.current !== route) return;
        const billPayments = (all || [])
          .filter((p) => String(p.bill_id) == String(purchase.bill_id))
          .sort(
            (a, b) =>
              ymdToTime(a.vendor_payment_date) -
              ymdToTime(b.vendor_payment_date),
          );
        setVendorPayments(billPayments);
      })
      .catch(() => { if (routeIdentityRef.current === route) setVendorPayments([]); });
  }, [purchase, company_id, id]);

  useEffect(() => {
    if (!company_id) return;

    apiGet<{
      closed_through_date?: string | null;
    }>(`/settings/financial-year-end?company_id=${company_id}`)
      .then((res) => {
        setClosedThroughDate(res?.closed_through_date || null);
      })
      .catch(() => {
        setClosedThroughDate(null);
      });
  }, [company_id]);

  /* ---- Derived ---- */
  const totalPaid = useMemo(
    () =>
      vendorPayments.reduce((sum, p) => {
        const v =
          typeof p.amount === "number" ? p.amount : Number(p.amount || 0);
        return sum + (isFinite(v) ? v : 0);
      }, 0),
    [vendorPayments],
  );

  const isPaid =
    !!purchase &&
    (Math.abs(totalPaid - purchase.amount) < 0.005 ||
      purchase.status?.toLowerCase() === "paid" ||
      purchase.status?.toLowerCase() === "closed");

  const outstanding = purchase ? Math.max(0, purchase.amount - totalPaid) : 0;

  const isClosedPaymentDate = useMemo(() => {
    return isDateClosed(paymentDate, closedThroughDate);
  }, [paymentDate, closedThroughDate]);

  // Prefill payment amount with outstanding
  useEffect(() => {
    if (!isPaid && !purchase?.voided && !recovery.ticket && !recovery.problem) {
      setPaymentAmount(outstanding > 0 ? toInputMoney(outstanding) : "");
    }
  }, [outstanding, isPaid, purchase?.voided, recovery.ticket, recovery.problem]);


  const paymentAccounts = useMemo(
    () =>
      accounts
        .filter(
          (a) =>
            (a.kind?.toLowerCase() === "bank" ||
              a.kind?.toLowerCase() === "cash") &&
            !a.is_system,
        )
        .slice()
        .sort(
          (a, b) =>
            paymentAccountWeight(a) - paymentAccountWeight(b) ||
            a.name.localeCompare(b.name),
        ),
    [accounts],
  );

  const bubbles = getPurchaseBubbles(purchase, totalPaid);
  const hasPayments = vendorPayments.length > 0;
  const sourceDocument = purchase?.source_document || null;

  // Hard stops ONLY: voided / has payments (Purchase rule)
  const baseEditable = !!purchase && !purchase.voided && !hasPayments;
  const canEdit = baseEditable;

  const editDisallowedReason = !baseEditable
    ? purchase?.voided
      ? localMessage("purchases.errors.voided")
      : hasPayments
        ? localMessage("purchases.errors.editPaid")
        : ""
    : "";

  /* ---- Handlers ---- */
  const handleSaveMemo = async () => {
    if (!purchase || !company_id) return;
    setMemoSaving(true);
    setMemoError(null);
    try {
      await apiPatch(
        `/purchases/${purchase.id}/memo?company_id=${company_id}`,
        {
          memo: memoDraft,
        },
      );
      setPurchase((prev) => (prev ? { ...prev, memo: memoDraft } : prev));
      setEditingMemo(false);
    } catch {
      setMemoError(localMessage("purchases.errors.memo"));
    } finally {
      setMemoSaving(false);
    }
  };

  const handleAddPayment = async () => {
    if (paymentDocumentRoute !== routeIdentity || !purchase || purchase.voided || String(purchase.id) !== id || !company_id || !billId || paying) return;
    setPaymentError(null);
    if (recovery.problem === "corrupt") return;
    const body = paymentBody();
    if (recovery.ticket && !recovery.exact(body)) {
      setPaymentError(localMessage("vendorPayments.retry.uncertain"));
      return;
    }
    if (!recovery.exact(body)) {
      const amount = Number(body.amount);
      if (!Number.isFinite(amount) || amount <= 0) {
        setPaymentError(localMessage("purchases.errors.enterPayment"));
        return;
      }
      if (amount > outstanding + 0.005) {
        setPaymentError(localMessage("purchases.errors.overpayment"));
        return;
      }
      if (!paymentAccountId) {
        setPaymentError(localMessage("purchases.errors.selectPaymentAccountRequired"));
        return;
      }

    }
    const operation = recovery.begin(body);
    if (!operation) return;
    try {
      await apiPost("/ar-ap/vendor-payments", {
        ...operation.attempt.submittedPayload,
        idempotency_key: operation.attempt.idempotencyKey,
      });
    } catch (error: any) {
      const kind = classifyVendorPaymentFailure(error, isClosedPeriodError);
      if (kind === "ambiguous") operation.uncertain();
      else operation.finish(); // Definite result clears the captured slot before showing it.
      if (!operation.isCurrent()) return;
      if (kind === "closed-period") setPaymentError(localClosedPeriod(closedThroughDate));
      else if (kind === "idempotency-conflict") setPaymentError(localMessage("vendorPayments.retry.conflict"));
      else if (kind === "voided") setPaymentError(localMessage("vendorPayments.retry.voided"));
      else if (kind === "unavailable") setPaymentError(localMessage("vendorPayments.retry.unavailable"));
      else if (kind === "ambiguous") setPaymentError(localMessage("vendorPayments.retry.uncertain"));
      else setPaymentError(localApiDetail(error) || localMessage("purchases.errors.recordPayment"));
      return;
    }
    operation.finish(true); // Terminal ticket, but keep repeat-tap protection through refresh.
    if (!operation.isCurrent()) { operation.release(); return; }
    setPaymentAmount("");
    try { await refreshPurchaseAndPayments(); }
    catch { if (operation.isCurrent()) setPaymentError(localMessage("purchases.errors.load")); }
    finally { operation.release(); }
  };

  // SaleDetail-clone edit click logic
  const handleEditClick = () => {
    if (!purchase) return;

    if (canEdit) {
      navigate(companyRoute(company_id!, `/purchases/${purchase.id}/edit`));
      return;
    }

    const reasons: PurchaseMessage[] = [];
    if (purchase?.voided) reasons.push(localMessage("purchases.errors.voidedSentence"));
    if (hasPayments) reasons.push(localMessage("purchases.errors.hasPayments"));
    if (ed && !ed.allowed && ed.reasons?.length) reasons.push(...ed.reasons);
    if (!reasons.length)
      reasons.push(localMessage("purchases.errors.editLocked"));

    setBlockedReasons(reasons);
    setShowBlockedEdit(true);
  };

  const handleDelete = async () => {
    if (!purchase || !company_id) return;

    setDeleteError(null);

    try {
      await apiDelete(`/purchases/${purchase.id}?company_id=${company_id}`);
      setShowDelete(false);
      navigate(companyRoute(company_id!, "/purchases"));
    } catch (e: any) {
      if (isClosedPeriodError(e)) {
        setDeleteError(localMessage("sales.errors.closedEdit"));
        return;
      }

      const detail = localApiDetail(e) || localMessage("purchases.errors.delete");
      setDeleteError(
        typeof detail === "string" ? formatErrorDates(detail) : detail,
      );
    }
  };

  const openSourceDocument = async () => {
    if (!sourceDocument?.id) return;

    setSourceDocumentError(null);
    try {
      const blob = await apiGetBlob(
        `/source-documents/${sourceDocument.id}/preview`,
      );
      const url = URL.createObjectURL(blob);
      setSourceDocumentPreview((prev) => {
        if (prev?.url) URL.revokeObjectURL(prev.url);
        return {
          url,
          contentType:
            blob.type || sourceDocument.content_type || "application/octet-stream",
        };
      });
    } catch {
      setSourceDocumentError(localMessage("purchases.errors.openDocument"));
    }
  };

  const downloadSourceDocument = async () => {
    if (!sourceDocument?.id) return;

    setSourceDocumentError(null);
    try {
      const blob = await apiGetBlob(
        `/source-documents/${sourceDocument.id}/download`,
      );
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download =
        sourceDocument.original_filename || `source-document-${sourceDocument.id}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      setSourceDocumentError(localMessage("purchases.errors.downloadDocument"));
    }
  };

  const closeSourceDocumentPreview = () => {
    setSourceDocumentPreview((prev) => {
      if (prev?.url) URL.revokeObjectURL(prev.url);
      return null;
    });
  };

  /* ---- Early returns ---- */
  if (loading)
    return (
      <div className={`text-center py-6 ${A.body} text-gray-500`}>
        {t("purchases.detail.loading")}
      </div>
    );
  if (error)
    return (
      <div className={`text-center py-6 ${A.body} text-red-500`}>{messageText(error)}</div>
    );
  if (!purchase)
    return (
      <div className={`text-center py-6 ${A.body} text-gray-500`}>
        {t("purchases.detail.notFound")}
      </div>
    );

  const invoiceYmd = (purchase?.date || "").trim();

  const headerDueDate =
    purchase?.due_date && purchase.due_date.trim()
      ? purchase.due_date.trim()
      : undefined;

  const dueYmd = (headerDueDate || "").trim();

  // "On account" in UI terms = due date is meaningful (different from invoice date)
  const showDueDate =
    !!dueYmd && !!invoiceYmd && dueYmd !== invoiceYmd && !purchase?.voided;

  const dueDateForHeader = showDueDate ? headerDueDate : undefined;

  const inputRight = `${A.inputBase} px-3 text-right tabular-nums`;
  const infoValue = `${A.inputBase} bg-gray-50 text-gray-700 flex items-center`;

  const iconBtn =
    `${A.iconBtn} inline-flex items-center justify-center ` +
    "hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-appleblueD";

  // Desktop scanned-invoice action buttons -- mirrors SaleInfoHeader's
  // InvoiceActionGroup `miniBtn` styling exactly.
  const scannedInvoiceBtn =
    "inline-flex items-center gap-1 rounded-xl border border-gray-200 bg-white " +
    "px-2.5 py-1.5 text-xs font-medium leading-none text-gray-700 " +
    "hover:bg-gray-50 active:bg-gray-100 transition " +
    "focus:outline-none focus:ring-2 focus:ring-appleblueD focus:border-appleblue " +
    "disabled:opacity-50 disabled:cursor-not-allowed";

  // Mobile scanned-invoice icon buttons -- Sale Detail's exact `miniBtn`
  // pill dimensions (px-2.5 py-1.5, no fixed height/width), icon-only, with
  // the same w-3.5 h-3.5 icon scale.
  const scannedInvoiceMobileBtn =
    "inline-flex items-center justify-center rounded-xl border border-gray-200 bg-white px-2.5 py-1.5 " +
    "hover:bg-gray-50 active:bg-gray-100 transition " +
    "focus:outline-none focus:ring-2 focus:ring-appleblueD focus:border-appleblue " +
    "disabled:opacity-50 disabled:cursor-not-allowed";

  /* =========================
     Render
     ========================= */
  return (
    <div
      className={`min-h-screen bg-gray-50 flex flex-col items-center pt-6 pb-10 px-2 ${A.body}`}
    >
      {sourceDocumentPreview && (
        <div
          className="fixed inset-0 z-50 bg-black/40 px-3 py-4 sm:px-6"
          role="dialog"
          aria-modal="true"
          aria-label={t("purchases.detail.preview")}
        >
          <div className="mx-auto flex h-full max-w-5xl flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-xl">
            <div className="flex items-center justify-between border-b border-gray-100 px-3 py-2">
              <div className={`${A.label} font-medium text-gray-600`}>
                {t("purchases.detail.scannedInvoice")}
              </div>
              <button
                type="button"
                onClick={closeSourceDocumentPreview}
                className={`${iconBtn} group`}
                aria-label={t("purchases.detail.closePreview")}
                title={t("directory.common.close")}
              >
                <XMarkIcon className="h-5 w-5 text-gray-400 group-hover:text-gray-600 transition" />
              </button>
            </div>

            <div className="min-h-0 flex-1 bg-gray-50">
              {sourceDocumentPreview.contentType
                .toLowerCase()
                .startsWith("image/") ? (
                <div className="flex h-full items-center justify-center overflow-auto p-3">
                  <img
                    src={sourceDocumentPreview.url}
                    alt={t("purchases.detail.scannedInvoice")}
                    className="max-h-full max-w-full rounded-xl object-contain"
                  />
                </div>
              ) : sourceDocumentPreview.contentType.toLowerCase() ===
                "application/pdf" ? (
                <object
                  data={sourceDocumentPreview.url}
                  type="application/pdf"
                  className="h-full w-full"
                >
                  <div className="flex h-full items-center justify-center p-4 text-center text-xs text-gray-500">
                    {t("purchases.detail.pdfUnavailable")}
                  </div>
                </object>
              ) : (
                <div className="flex h-full items-center justify-center p-4 text-center text-xs text-gray-500">
                  {t("purchases.detail.previewUnavailable")}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="sticky top-0 z-30 bg-gray-50 w-full max-w-6xl flex items-center border-b border-gray-100 px-2 pb-2 mb-2">
        <div className="flex-none">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className={`${A.iconBtn} flex items-center justify-center hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-appleblueD`}
            aria-label={t("directory.common.back")}
          >
            <ArrowLeftIcon className="w-6 h-6 text-gray-400 hover:text-gray-600 transition" />
          </button>
        </div>

        <div className="flex-1 flex justify-center pointer-events-none relative">
          <h2 className={A.title + " text-center"}>{t("purchases.detail.title")}</h2>
        </div>

        <div className="flex-none ml-auto">
          <button
            type="button"
            onClick={() => navigate(companyRoute(company_id!, "/"))}
            className={`${A.iconBtn} flex items-center justify-center hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-appleblueD z-10`}
            aria-label={t("directory.common.close")}
          >
            <XMarkIcon className="w-6 h-6 text-gray-400 hover:text-gray-600 transition" />
          </button>
        </div>
      </div>

      {/* Main Card */}
      <div
        className={`${A.card} w-full max-w-3xl mx-auto px-5 sm:px-6 py-5 sm:py-6`}
      >
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0 flex-1">
            <PurchaseInfoHeader
              purchaseLabels={t}
              date={purchase.date}
              invoiceNumber={purchase.invoice_number || purchase.invoice_id}
              vendor={purchase.vendor?.name}
              email={purchase.vendor?.email}
              mobile={purchase.vendor?.mobile}
              statusBubbles={bubbles}
              outstanding={outstanding}
              dueDate={dueDateForHeader}
              total={purchase.amount}
              isPaid={!!isPaid}
              edited={purchase.edited && !purchase.voided}
              lastEditedAt={purchase.last_edited_at}
              statusRowEndSlot={
                sourceDocument ? (
                  <span className="inline-flex items-center gap-2 sm:hidden">
                    <button
                      type="button"
                      className={scannedInvoiceMobileBtn}
                      aria-label={t("purchases.detail.viewScan")}
                      title={t("purchases.detail.viewScan")}
                      onClick={openSourceDocument}
                    >
                      <PaperClipIcon
                        className="w-3.5 h-3.5 text-gray-400"
                        aria-hidden="true"
                      />
                    </button>

                    <button
                      type="button"
                      className={scannedInvoiceMobileBtn}
                      aria-label={t("purchases.detail.downloadScan")}
                      title={t("purchases.detail.downloadScan")}
                      onClick={downloadSourceDocument}
                    >
                      <ArrowDownTrayIcon
                        className="w-3.5 h-3.5 text-gray-400"
                        aria-hidden="true"
                      />
                    </button>
                  </span>
                ) : null
              }
            />
          </div>

          {/* Scanned invoice actions (desktop) -- matches Sale Detail's
              labelled top-right invoice action group. */}
          {sourceDocument && (
            <div className="hidden shrink-0 sm:flex sm:items-center sm:gap-2 sm:self-start sm:ml-4">
              <button
                type="button"
                onClick={openSourceDocument}
                className={scannedInvoiceBtn}
                aria-label={t("purchases.detail.viewScan")}
                title={t("purchases.detail.viewScan")}
              >
                <EyeIcon className="w-3.5 h-3.5 text-gray-400" aria-hidden="true" />
                <span>{t("purchases.detail.view")}</span>
              </button>

              <button
                type="button"
                onClick={downloadSourceDocument}
                className={scannedInvoiceBtn}
                aria-label={t("purchases.detail.downloadScan")}
                title={t("purchases.detail.downloadScan")}
              >
                <ArrowDownTrayIcon
                  className="w-3.5 h-3.5 text-gray-400"
                  aria-hidden="true"
                />
                <span>{t("purchases.detail.download")}</span>
              </button>
            </div>
          )}
        </div>

        {sourceDocumentError && (
          <div className="mb-4 text-xs font-medium text-red-600">
            {messageText(sourceDocumentError)}
          </div>
        )}

        {/* Lines - Desktop Table */}
        <div className="hidden sm:block mb-5">
          <div className="rounded-2xl border border-gray-200 bg-white overflow-hidden">
            <table className="w-full text-[13px] text-gray-700">
              <thead className="bg-gray-50">
                <tr className="text-xs text-gray-500">
                  <th className="text-left px-3 py-2 font-normal">{t("directory.products.product")}</th>
                  <th className="text-right px-3 py-2 font-normal tabular-nums">
                    {t("sales.common.qty")}
                  </th>
                  <th className="text-right px-3 py-2 font-normal tabular-nums">
                    {t("purchases.common.costPrice")}
                  </th>
                  <th className="text-right px-3 py-2 font-normal tabular-nums">
                    {t("sales.common.discount")}
                  </th>
                  <th className="text-right px-3 py-2 font-normal tabular-nums">
                    {t("sales.common.subtotal")}
                  </th>
                </tr>
              </thead>

              <tbody className="divide-y divide-gray-100">
                {purchase.lines.map((line) => (
                  <tr key={line.id}>
                    <td className="px-3 py-2">{line.product_name || "—"}</td>
                    <td className="text-right px-3 py-2 tabular-nums">
                      {line.qty}
                    </td>
                    <td className="text-right px-3 py-2 tabular-nums">
                      {formatMoney(line.cost_price)}
                    </td>
                    <td className="text-right px-3 py-2 tabular-nums">
                      {typeof line.discount === "number" &&
                      line.discount > 0 ? (
                        <span>{line.discount}%</span>
                      ) : (
                        <span className="text-gray-300 select-none">—</span>
                      )}
                    </td>
                    <td className="text-right px-3 py-2 tabular-nums font-medium">
                      {formatMoney(calcSubtotal(line))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Lines - Mobile Cards */}
        <div className="block sm:hidden space-y-2 mb-5">
          {purchase.lines.map((line) => (
            <div
              key={line.id}
              className="rounded-2xl border border-gray-200 bg-white shadow-sm px-4 py-3 text-[13px] text-gray-700"
            >
              <div className="text-[13px] font-medium text-gray-700 mb-1">
                {line.product_name || "—"}
              </div>

              <div className="flex justify-between">
                <span className="text-xs text-gray-500">{t("sales.common.qty")}</span>
                <span className="tabular-nums">{line.qty}</span>
              </div>

              <div className="flex justify-between mt-1">
                <span className="text-xs text-gray-500">{t("purchases.common.costPrice")}</span>
                <span className="tabular-nums">
                  {formatMoney(line.cost_price)}
                </span>
              </div>

              <div className="flex justify-between mt-1">
                <span className="text-xs text-gray-500">{t("sales.common.discount")}</span>
                <span className="tabular-nums">
                  {typeof line.discount === "number" && line.discount > 0
                    ? `${line.discount}%`
                    : "—"}
                </span>
              </div>

              <div className="flex justify-between mt-1">
                <span className="text-xs text-gray-500">{t("sales.common.subtotal")}</span>
                <span className="tabular-nums font-medium">
                  {formatMoney(calcSubtotal(line))}
                </span>
              </div>
            </div>
          ))}
        </div>

        {/* Total */}
        <div className="mb-4 text-right text-[13px] text-gray-500">
          {t("sales.common.totalWithColon")}{" "}
          <span className="font-semibold text-gray-700 tabular-nums">
            {formatMoney(purchase.amount)}
          </span>
        </div>

        {/* Payment History */}
        {vendorPayments.length > 0 && (
          <div className="mb-5">
            <div className={A.label + " mb-2"}>{t("purchases.detail.paymentHistory")}</div>

            {/* Desktop */}
            <div className="hidden sm:block">
              <div className="rounded-2xl border border-gray-200 bg-white overflow-hidden">
                <table className="w-full text-[13px] text-gray-700">
                  <thead className="bg-gray-50">
                    <tr className="text-xs text-gray-500">
                      <th className="text-left px-3 py-2 font-normal">{t("sales.common.date")}</th>
                      <th className="text-left px-3 py-2 font-normal">
                        {t("purchases.detail.paymentAccount")}
                      </th>
                      <th className="text-right px-3 py-2 font-normal tabular-nums">
                        {t("sales.common.amount")}
                      </th>
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-gray-100">
                    {vendorPayments.map((p, i) => {
                      const acct = accounts.find(
                        (a) => a.id === p.payment_account_id,
                      );
                      const amountVal =
                        typeof p.amount === "number"
                          ? p.amount
                          : Number(p.amount || 0);

                      return (
                        <tr key={p.id || i}>
                          <td className="px-3 py-2">
                            {formatAnyDateToLocale(p.vendor_payment_date)}
                          </td>
                          <td className="px-3 py-2">
                            {acct ? acct.name : "—"}
                          </td>
                          <td className="text-right px-3 py-2 tabular-nums">
                            {formatMoney(amountVal)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Mobile */}
            <div className="block sm:hidden space-y-2">
              {vendorPayments.map((p, i) => {
                const acct = accounts.find(
                  (a) => a.id === p.payment_account_id,
                );
                const amountVal =
                  typeof p.amount === "number"
                    ? p.amount
                    : Number(p.amount || 0);

                return (
                  <div
                    key={p.id || i}
                    className="rounded-2xl border border-gray-200 bg-white shadow-sm px-4 py-3 text-[13px] text-gray-700"
                  >
                    <div className="flex justify-between">
                      <span className="text-xs text-gray-500">{t("sales.common.date")}</span>
                      <span className="font-medium tabular-nums">
                        {formatAnyDateToLocale(p.vendor_payment_date)}
                      </span>
                    </div>
                    <div className="flex justify-between mt-1">
                      <span className="text-xs text-gray-500">{t("purchases.detail.paymentAccount")}</span>
                      <span className="font-medium">
                        {acct ? acct.name : "—"}
                      </span>
                    </div>
                    <div className="flex justify-between mt-1">
                      <span className="text-xs text-gray-500">{t("sales.common.amount")}</span>
                      <span className="font-medium tabular-nums">
                        {formatMoney(amountVal)}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="text-right text-[13px] text-gray-500 mt-2">
              {t("sales.common.paidTotal")}{" "}
              <span className="font-semibold text-gray-700 tabular-nums">
                {formatMoney(totalPaid)}
              </span>
            </div>
          </div>
        )}

        {/* Add Payment */}
        {(!isPaid || recovery.ticket || recovery.problem) && !purchase.voided && (
          <div className="bg-gray-50 rounded-2xl px-4 py-4 ring-1 ring-black/5 mb-4 w-full">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <label className={A.label + " mb-1 block"}>{t("sales.common.paymentDate")}</label>

                <AppleDateInput
                  openCalendarLabel={t("sales.shared.openCalendar")}
                  valueYmd={paymentDate}
                  onChangeYmd={setPaymentDate}
                  allowEmpty={false}
                  maxYmd={ymdTodayLocal()}
                  ariaLabel={t("purchases.detail.paymentDateAria")}
                />
                {isClosedPaymentDate && (
                  <div className="mt-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-[13px] text-amber-800">
                    {t("sales.errors.closedWarning")}
                  </div>
                )}
              </div>

              <div>
                <label className={A.label + " mb-1 block"}>{t("purchases.detail.paymentAccount")}</label>
                <AppleComboBox
                  emptyText={t("sales.shared.noResults")}
                  toggleOptionsLabel={t("sales.shared.toggleOptions")}
                  options={paymentAccounts}
                  value={
                    paymentAccounts.find(
                      (acc) => acc.id === paymentAccountId,
                    ) || null
                  }
                  onChange={(acc) => setPaymentAccountId(acc ? acc.id : null)}
                  getOptionLabel={(acc) => acc.name}
                  placeholder={t("purchases.detail.selectPaymentAccount")}
                />
              </div>

              <div>
                <label className={A.label + " mb-1 block"}>{t("sales.common.amount")}</label>
                <AppleNumberInput
                  value={paymentAmount}
                  onChange={(e) => setPaymentAmount(e.target.value)}
                  onBlur={() => {
                    if (!paymentAmount || paymentAmount.trim() === "") return;

                    const amt = parseMoneyInput(paymentAmount);

                    if (!recovery.exact(paymentBody()) && amt > outstanding + 0.005) {
                      setPaymentError(localMessage("purchases.errors.overpayment"));
                      return;
                    }

                    if (amt < 0) {
                      setPaymentAmount("0");
                      return;
                    }

                    setPaymentError(null);
                    setPaymentAmount(toInputMoney(amt));
                  }}
                  className={inputRight}
                  placeholder={outstanding > 0 ? toInputMoney(outstanding) : ""}
                  autoComplete="off"
                />
              </div>
            </div>

            {recovery.problem === "corrupt" && (
              <Button onClick={() => { recovery.startNew(); setPaymentError(null); }}>
                {t("vendorPayments.retry.startNew")}
              </Button>
            )}
            {editedPayment && !recovery.problem && (
              <Button onClick={() => { recovery.restore(); setPaymentError(null); }} disabled={paying}>
                {t("vendorPayments.retry.restore")}
              </Button>
            )}
            <div className="flex justify-end mt-4">
              <Button
                onClick={handleAddPayment}
                disabled={paying || !paymentAccountId || recovery.problem === "corrupt"}
                variant="primary"
                minWidth="120px"
              >
                {paying ? t("directory.common.savingEllipsis") : recovery.ticket ? t("vendorPayments.retry.retry") : t("purchases.detail.addPayment")}
              </Button>
            </div>

            {(recoveryMessage || paymentError) && (
              <div className="text-xs text-red-500 mt-2">{messageText(recoveryMessage || paymentError)}</div>
            )}
          </div>
        )}

        {/* ===== Memo (SaleDetail flow cloned) =====
            - If purchase IS editable => NO pencil, view-only block
            - If purchase is NOT editable => show pencil, allow inline edit via pull-down
        */}
        {!editingMemo ? (
          <MemoSection
            label={t("sales.common.memo")}
            editAriaLabel={t("sales.common.editMemo")}
            mode="view"
            variant="card"
            value={purchase.memo || ""}
            emptyText={t("purchases.detail.noMemo")}
            className="mb-6"
            onEditClick={
              !canEdit
                ? () => {
                    setEditingMemo(true);
                    setMemoDraft(purchase.memo || "");
                    setMemoError(null);
                  }
                : undefined
            }
          />
        ) : (
          <AppleCard padding="p-4" className="mb-6">
            <MemoSection
              mode="edit"
              variant="plain"
              label={t("sales.common.memo")}
              value={memoDraft}
              onChange={setMemoDraft}
              rows={2}
              disabled={memoSaving}
              placeholder={t("sales.common.memoPlaceholder")}
            />

            <div className="flex gap-3 justify-end mt-3">
              <Button
                variant="secondary"
                type="button"
                onClick={() => {
                  setEditingMemo(false);
                  setMemoDraft(purchase.memo || "");
                  setMemoError(null);
                }}
                disabled={memoSaving}
              >
                {t("directory.common.cancel")}
              </Button>

              <Button
                variant="primary"
                type="button"
                onClick={handleSaveMemo}
                disabled={memoSaving}
              >
                {memoSaving ? t("directory.common.savingEllipsis") : t("directory.common.save")}
              </Button>
            </div>

            <div className="min-h-[18px] mt-2">
              {memoError ? (
                <div className="text-xs text-red-500 font-medium">
                  {messageText(memoError)}
                </div>
              ) : null}
            </div>
          </AppleCard>
        )}

        {/* ===== Bottom actions (SaleDetail style) ===== */}
        <div className="flex justify-end gap-3 mt-6">
          <Button
            variant={canEdit ? "outline" : "secondary"}
            type="button"
            onClick={handleEditClick}
            aria-disabled={!canEdit}
            title={canEdit ? undefined : messageText(editDisallowedReason)}
            className={canEdit ? "" : "bg-gray-50 text-gray-400"}
          >
            {t("directory.common.edit")}
          </Button>

          <Button
            variant="secondary"
            type="button"
            onClick={() => {
              if (!purchase.voided) {
                setDeleteError(null);
                setShowDelete(true);
              }
            }}
            disabled={!!purchase.voided}
            title={purchase.voided ? t("purchases.detail.alreadyVoided") : t("purchases.detail.void")}
          >
            {purchase.voided ? t("purchases.status.voided") : t("purchases.detail.void")}
          </Button>
        </div>

        {/* Delete Modal */}
        {showDelete && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 px-3">
            <div className="w-full max-w-sm bg-white border border-gray-200 rounded-2xl shadow-md p-4">
              <div className="text-[15px] font-semibold text-gray-700">
                {t("purchases.detail.deleteConfirm")}
              </div>
              <div className="text-[13px] text-gray-600 mt-1">
                {t("purchases.detail.voidExplain")}
              </div>
              <div className="text-[13px] text-gray-600 mt-1">
                {t("directory.common.irreversible")}
              </div>

              {deleteError && (
                <div className="mt-3 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
                  {messageText(deleteError)}
                </div>
              )}

              <div className="flex justify-end gap-3 mt-4">
                <Button
                  variant="secondary"
                  type="button"
                  onClick={() => {
                    setDeleteError(null);
                    setShowDelete(false);
                  }}
                >
                  {t("directory.common.cancel")}
                </Button>

                <Button
                  variant="primary"
                  type="button"
                  className="bg-red-600 hover:bg-red-700"
                  onClick={handleDelete}
                >
                  {t("purchases.detail.void")}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Blocked Edit Modal */}
        {showBlockedEdit && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 px-3">
            <div className="w-full max-w-md bg-white border border-gray-200 rounded-2xl shadow-md p-5">
              <div className="text-[15px] font-semibold mb-2 text-gray-800">
                {t("purchases.detail.editNotAllowed")}
              </div>

              <ul className="list-disc pl-5 text-[13px] text-gray-700 space-y-1">
                {blockedReasons.map((r, i) => (
                  <li key={i}>{messageText(r)}</li>
                ))}
              </ul>

              <div className="flex justify-end mt-4">
                <Button
                  variant="secondary"
                  type="button"
                  onClick={() => setShowBlockedEdit(false)}
                >
                  {t("purchases.common.ok")}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
